import logger from './logger';
import { clearAllDekLogoutPending } from '../modules/logout/Logout.helpers';

const CryptoJS = require('crypto-js');

const basePath = window.__ENV.REACT_APP_BASE_PATH || '/application';
const DEK_SILENT_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];
const DEK_RENEW_AHEAD_MS = 30000;
const DEK_MIN_RENEW_DELAY_MS = 5000;
const createSharedState = () => ({
  sessionKey: null,
  keyExpiry: null,
  sessionId: null,
  sessionKeyPromise: null,
  rebootstrapPromise: null,
  prewarmPromise: null,
  renewTimerId: null,
  decryptLogState: {
    lastAt: 0,
    suppressed: 0,
  },
});

let sharedState = createSharedState();

class CryptoService {
  static resetSharedState() {
    sharedState = createSharedState();
  }

  constructor() {
    this.shared = sharedState;
  }

  clearRenewTimer() {
    if (this.shared.renewTimerId) {
      clearTimeout(this.shared.renewTimerId);
      this.shared.renewTimerId = null;
    }
  }

  scheduleRenew() {
    this.clearRenewTimer();
    if (!this.shared.keyExpiry) {
      return;
    }

    const renewInMs = Math.max(
      DEK_MIN_RENEW_DELAY_MS,
      this.shared.keyExpiry - Date.now() - DEK_RENEW_AHEAD_MS,
    );

    this.shared.renewTimerId = setTimeout(async () => {
      try {
        // Force a refresh in the background so user actions avoid bootstrap latency.
        console.info('[DEK] background renewal starting');
        this.shared.sessionKey = null;
        this.shared.keyExpiry = null;
        this.shared.sessionId = null;
        await this.getSessionKey();
        console.info('[DEK] background renewal succeeded');
      } catch (error) {
        // Keep failures silent here; foreground callers will surface actionable errors.
        console.warn(`[DEK] background renewal failed status=${error?.status} message=${error?.message}`);
      }
    }, renewInMs);
  }

  async prewarmSessionKey() {
    if (this.shared.prewarmPromise) {
      return this.shared.prewarmPromise;
    }

    console.info('[DEK] prewarm started');
    this.shared.prewarmPromise = (async () => {
      try {
        await this.getSessionKey();
        console.info('[DEK] prewarm succeeded');
      } catch (error) {
        // Silent prewarm - foreground paths still handle errors explicitly.
        console.warn(`[DEK] prewarm failed status=${error?.status} message=${error?.message}`);
        if (error?.status === 429) {
          // Rate-limited — wait for the window to reset then retry once.
          // prewarmPromise stays alive during the wait so concurrent callers
          // share this promise rather than firing more requests.
          // Normally the server resets the rate-limit bucket on logout-cleanup so
          // this path should be rare; 10s is a conservative fallback.
          const retryMs = error?.retryAfterMs ?? 10000;
          console.warn(`[DEK] prewarm rate-limited — retrying in ${retryMs}ms`);
          await CryptoService.sleep(retryMs);
          try {
            await this.getSessionKey();
            console.info('[DEK] prewarm succeeded after rate-limit retry');
          } catch (retryError) {
            console.warn(`[DEK] prewarm rate-limit retry failed status=${retryError?.status} message=${retryError?.message}`);
          }
        }
      } finally {
        this.shared.prewarmPromise = null;
      }
    })();

    return this.shared.prewarmPromise;
  }

  // Re-register the DEK with the server from scratch.
  // Called when the server loses the DEK (e.g. cookies cleared, pod restart)
  // and returns 415 on a save operation.
  // Deduplicated: concurrent callers wait on the same rebootstrap rather than
  // racing to register two different DEKs (which causes cookie/key mismatch).
  async rebootstrap() {
    if (this.shared.rebootstrapPromise) {
      return this.shared.rebootstrapPromise;
    }
    this.shared.rebootstrapPromise = (async () => {
      this.clearRenewTimer();
      this.shared.sessionKey = null;
      this.shared.keyExpiry = null;
      this.shared.sessionId = null;
      this.shared.sessionKeyPromise = null;
      console.info('[DEK] rebootstrap triggered — re-registering DEK with server');
      return this.getSessionKey();
    })();
    try {
      return await this.shared.rebootstrapPromise;
    } finally {
      this.shared.rebootstrapPromise = null;
    }
  }

  static isRetryableDekStatus(status) {
    return status === 401 || status === 403 || status >= 500;
  }

  static sleep(ms) {
    return new Promise(resolve => { setTimeout(resolve, ms); });
  }

  logDecryptionFailure(error, prefix = 'Decryption failed:') {
    const now = Date.now();
    const throttleMs = 15000;
    const state = this.shared.decryptLogState || { lastAt: 0, suppressed: 0 };

    if (now - state.lastAt >= throttleMs) {
      if (state.suppressed > 0) {
        logger.warn(`Suppressed ${state.suppressed} additional decryption errors`);
      }
      logger.warn(prefix, error);
      state.lastAt = now;
      state.suppressed = 0;
      this.shared.decryptLogState = state;
      return;
    }

    state.suppressed += 1;
    this.shared.decryptLogState = state;
  }

  async getSessionKey() {
    if (this.shared.sessionKeyPromise) {
      return this.shared.sessionKeyPromise;
    }

    // Check if key exists and is not expired
    if (!this.shared.sessionKey || this.isKeyExpired()) {
      this.shared.sessionKeyPromise = (async () => {
        const registerSessionKey = async () => {
          // Generate a new sessionId and DEK, then register the DEK with the server.
          // The server will validate the authenticated session and persist a wrapped DEK.
          const sessionId = CryptoJS.lib.WordArray.random(16).toString();
          const dek = CryptoJS.lib.WordArray.random(32).toString();

          // Ensure we have a CSRF token (server provides at GET /csrf). The server
          // sets a CSRF cookie and also returns the token in JSON. Fetch it first
          // and include the token header on the DEK registration POST.
          let csrfToken = null;
          try {
            const tokenResp = await fetch(`${basePath}/csrf`, { method: 'GET', credentials: 'include' });
            if (tokenResp.ok) {
              const tBody = await tokenResp.json();
              csrfToken = tBody && tBody.token;
            }
          } catch (e) {
            // ignore; POST will fail with 403 if missing, but we keep attempt
          }

          const headers = {
            'Content-Type': 'application/json',
          };
          // Preferred header name accepted by csurf middleware
          if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
          // Also include legacy header name some clients expect
          if (csrfToken) headers['CSRF-Token'] = csrfToken;
          // If we couldn't fetch token JSON, try reading cookie set by server
          if (!csrfToken) {
            try {
              const match = document.cookie.match(/(?:^|;)\s*CSRF-Token=([^;]+)/);
              if (match) {
                headers['X-CSRF-Token'] = decodeURIComponent(match[1]);
              }
            } catch (e) {
              // ignore
            }
          }

          // Server no longer exposes a public key endpoint. Send the plaintext DEK
          // to the server over TLS with the authenticated Forgerock session cookie.
          const response = await fetch(`${basePath}/dek`, {
            method: 'POST',
            credentials: 'include', // send auth cookie
            headers,
            body: JSON.stringify({ sessionId, dek }),
          });

          if (!response.ok) {
            const error = new Error(`Failed to register session key (status: ${response.status})`);
            error.status = response.status;
            error.isDekError = true;
            // Read rate-limit reset time so prewarm can wait the right amount before retrying.
            if (response.status === 429) {
              const resetHeader = response.headers.get('RateLimit-Reset') || response.headers.get('Retry-After');
              if (resetHeader) {
                error.retryAfterMs = (parseInt(resetHeader, 10) + 1) * 1000;
              }
            }
            // Parse reason from server so we can skip retries on genuinely invalid sessions
            try {
              const errBody = await response.json();
              if (errBody?.reason) error.reason = errBody.reason;
            } catch (e) { /* ignore parse errors */ }
            throw error;
          }

          const body = await response.json();
          // server returns { sessionId, expiresAt }
          this.shared.sessionId = sessionId;
          this.shared.sessionKey = dek;
          this.shared.keyExpiry = body.expiresAt || (Date.now() + (2 * 60 * 1000));
           this.scheduleRenew();
           // Clear all DEK-logout-pending keys on successful registration so that
           // future session-expiry events are handled normally.
           clearAllDekLogoutPending();
           return this.shared.sessionKey;
        };

        let lastError;
        for (let attempt = 0; attempt <= DEK_SILENT_RETRY_DELAYS_MS.length; attempt += 1) {
          try {
            // eslint-disable-next-line no-await-in-loop
            return await registerSessionKey();
          } catch (error) {
            lastError = error;
            const status = error?.status;
            const reason = error?.reason;
            const retriesExhausted = attempt >= DEK_SILENT_RETRY_DELAYS_MS.length;
            // reason=missing means ForgeRock cookie is absent — retrying won't make it
            // appear. Skip all retries and fail immediately so the session-expired handler
            // can redirect to a full logout+re-auth without wasting 15 seconds.
            const isMissingForgeRock = reason === 'missing';

            if (retriesExhausted || !CryptoService.isRetryableDekStatus(status) || isMissingForgeRock) {
              console.warn(`[DEK] registration failed (no retry) attempt=${attempt + 1} status=${status} reason=${reason}`);
              // Fire session-expired on exhausted 401 or immediate missing — pass reason
              // in detail so the handler can choose the right recovery action.
              if (status === 401 && (retriesExhausted || isMissingForgeRock)) {
                try {
                  window.dispatchEvent(new CustomEvent('dek:session-expired', { detail: { reason } }));
                } catch (e) { /* ignore in non-browser environments */ }
              }
              throw error;
            }
            const delay = DEK_SILENT_RETRY_DELAYS_MS[attempt] + Math.floor(Math.random() * 150);
            console.warn(`[DEK] registration failed, retrying attempt=${attempt + 1} status=${status} reason=${reason} retryDelayMs=${delay}`);
            // eslint-disable-next-line no-await-in-loop
            await CryptoService.sleep(delay);
          }
        }

        throw lastError || new Error('Failed to register session key');
      })();

      try {
        return await this.shared.sessionKeyPromise;
      } catch (error) {
        // Reset shared key state so next attempt can re-bootstrap cleanly.
        this.clearRenewTimer();
        this.shared.sessionKey = null;
        this.shared.keyExpiry = null;
        this.shared.sessionId = null;
        throw error;
      } finally {
        this.shared.sessionKeyPromise = null;
      }
    }

    return this.shared.sessionKey;
  }

  isKeyExpired() {
    return !this.shared.keyExpiry || Date.now() >= this.shared.keyExpiry;
  }

  async encryptWithMetadata(data) {
    try {
      const iv = CryptoJS.lib.WordArray.random(16);
      const sessionKey = await this.getSessionKey();

      const payload = {
        data,
        metadata: {
          timestamp: Date.now(),
          nonce: CryptoJS.lib.WordArray.random(16).toString(),
        },
      };

      // Encrypt the data
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(payload),
        sessionKey,
        {
          iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        },
      );

      return {
        data: encrypted.toString(),
        k: iv.toString(),
      };
    } catch (error) {
      logger.error('Encryption error:', error);
      const wrappedError = new Error('Failed to encrypt data');
      if (error?.isDekError) wrappedError.isDekError = true;
      if (error?.status) wrappedError.status = error.status;
      throw wrappedError;
    }
  }

  async decryptResponse(encryptedResponse) {
    try {
      const { data, k } = encryptedResponse;
      const sessionKey = await this.getSessionKey();

      const decrypted = CryptoJS.AES.decrypt(
        data,
        sessionKey,
        {
          iv: CryptoJS.enc.Hex.parse(k),
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        },
      );

      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedString);
    } catch (error) {
      this.logDecryptionFailure(error);
      throw new Error('Failed to decrypt response');
    }
  }

  async decryptWithMetadata(response) {
    try {
      const decrypted = await this.decryptResponse(response);

      // Verify timestamp if present
      if (decrypted.metadata?.timestamp) {
        const MAX_RESPONSE_AGE = 2 * 60 * 1000; // 2 minutes
        if (Date.now() - decrypted.metadata.timestamp > MAX_RESPONSE_AGE) {
          throw new Error('Response expired');
        }
      }

      return decrypted.data;
    } catch (error) {
      logger.error('Verification failed:', error);
      throw new Error('Failed to verify and decrypt response');
    }
  }

  async decryptStr(response) {
    let retryAttempted = false;
    const parseMaybeJson = value => {
      if (typeof value !== 'string') {
        return value;
      }
      const trimmed = value.trim();
      if (!trimmed) {
        return value;
      }
      if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"')) {
        try {
          return JSON.parse(trimmed);
        } catch (e) {
          return value;
        }
      }
      return value;
    };

    const decryptWithSessionKey = async sessionKey => {
      const decrypted = CryptoJS.AES.decrypt(response, sessionKey);
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      if (!decryptedString) {
        throw new Error('decrypted payload is empty');
      }
      return JSON.parse(decryptedString);
    };

    try {
      if (response == null) {
        throw new Error('ciphertext response is missing or invalid');
      }

      // Some endpoints/fields can already be plain JSON objects/strings
      if (typeof response === 'object') {
        return response;
      }

      const plainValue = parseMaybeJson(response);
      if (plainValue !== response) {
        return plainValue;
      }

      if (typeof response !== 'string') {
        throw new Error('ciphertext response is missing or invalid');
      }

      const sessionKey = await this.getSessionKey();

      try {
        return await decryptWithSessionKey(sessionKey);
      } catch (firstError) {
        // Session key might be stale. Retry once with fresh key.
        if (!retryAttempted) {
          retryAttempted = true;
          this.shared.sessionKey = null;
          this.shared.keyExpiry = null;
          this.shared.sessionId = null;
          try {
            const refreshedKey = await this.getSessionKey();
            return await decryptWithSessionKey(refreshedKey);
          } catch (retryError) {
            throw firstError;
          }
        }
        throw firstError;
      }
    } catch (error) {
      this.logDecryptionFailure(error);
      const detail = error?.message || 'unknown error';
      throw new Error(`Failed to decrypt response: ${detail}`);
    }
  }

  async encryptStr(data) {
    try {
      const sessionKey = await this.getSessionKey();
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), sessionKey).toString();
      return encrypted;
    } catch (error) {
      // console.error('Encryption failed:', error);
      const wrappedError = new Error(`Failed to encrypt data: ${error?.message || 'unknown'}`);
      if (error?.isDekError) wrappedError.isDekError = true;
      if (error?.status) wrappedError.status = error.status;
      throw wrappedError;
    }
  }
}

export default CryptoService;
